import { Component, OnInit, ViewEncapsulation, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { FuseConfigService } from '@fuse/services/config.service';
import { fuseAnimations } from '@fuse/animations';

import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { locale as en } from './../i18n/en';
import { locale as zh } from './../i18n/zh';
import { AuthService } from '@app/core/services/auth.service';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { takeUntil, debounceTime, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { confirmPasswordValidator } from '@app/shared/validators/confirm-password.validator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
    environment = environment;
    form: FormGroup;

    buttonOption: MatProgressButtonOptions = {
        active: false,
        text: '',
        buttonColor: 'accent',
        barColor: 'accent',
        raised: true,
        stroked: false,
        mode: 'indeterminate',
        value: 0,
        disabled: true
    };

    translations: string[];

    private unsubscribeAll: Subject<any>;

    constructor(
        private fuseConfigService: FuseConfigService,
        private formBuilder: FormBuilder,
        private fuseTranslationLoaderService: FuseTranslationLoaderService,
        private translateService: TranslateService,
        private authService: AuthService,
        public snackBar: MatSnackBar,
        private route: ActivatedRoute
    ) {
        // Configure the layout
        this.fuseConfigService.config = {
            layout: {
                navbar: {
                    hidden: true
                },
                toolbar: {
                    hidden: true
                },
                footer: {
                    hidden: true
                },
                sidepanel: {
                    hidden: true
                }
            }
        };

        this.fuseTranslationLoaderService.loadTranslations(en, zh);
        this.unsubscribeAll = new Subject();

        this.translateService
            .get([
                'auth.reset_password.send',
                'auth.reset_password.success_send',
                'auth.reset_password.invalid_token_or_uid'
            ])
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(translations => {
                this.translations = translations;
                this.buttonOption.text = translations['auth.reset_password.send'];
            });

        this.form = this.formBuilder.group({
            new_password1: ['', [Validators.required, Validators.minLength(6)]],
            new_password2: ['', [Validators.required, confirmPasswordValidator]]
        });
    }

    ngOnInit(): void {
        this.form.valueChanges
            .pipe(
                takeUntil(this.unsubscribeAll),
                debounceTime(150),
                tap(
                    () =>
                        (this.buttonOption.disabled =
                            this.form.pristine ||
                            this.form.invalid)
                )
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    send(): void {
        if (!this.buttonOption.disabled && !this.buttonOption.active) {
            this.buttonOption.active = true;

            this.authService.confirmResetPassword(
                Object.assign(this.form.value, {
                    uid: this.route.snapshot.paramMap.get('uid'),
                    token: this.route.snapshot.paramMap.get('token')
                }))
                .subscribe(
                    data => this.showSuccessMessage(this.translations['auth.reset_password.success_send']),
                    error => this.showErrorMessage(error.parsedMessage)
                );
        }
    }

    private showSuccessMessage(message: string): void {
        this.snackBar.open(message, '', {
            duration: 3000
        });

        this.buttonOption.active = false;
    }

    private showErrorMessage(error): void {
        if (error.includes('token') || error.includes('uid')) {
            error = this.translations['auth.reset_password.invalid_token_or_uid'];
        }

        this.snackBar.open(error, '', {
            duration: 3000,
            panelClass: ['warn']
        });

        this.buttonOption.active = false;
    }

}
